import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Briefcase, MapPin, DollarSign, Clock, Filter, Search, Plus,
  Loader2, User, Mail, Phone, CheckCircle, Send, ArrowRight,
  Droplet, Zap, Thermometer, Wrench, Sparkles, Car, X, ChevronDown,
  Home, Bug, Hammer, Trees, Truck, DoorOpen, Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceIcons = {
  plumbing: Droplet,
  electrical: Zap,
  hvac: Thermometer,
  handyman: Wrench,
  cleaning: Sparkles,
  mechanics: Car,
  roofing: Home,
  "pest-control": Bug,
  remodeling: Hammer,
  landscaping: Trees,
  moving: Truck,
  "garage-door": DoorOpen,
  "appliance-repair": Settings,
};

const serviceColors = {
  plumbing: "bg-blue-100 text-blue-700",
  electrical: "bg-yellow-100 text-yellow-700",
  hvac: "bg-cyan-100 text-cyan-700",
  handyman: "bg-orange-100 text-orange-700",
  cleaning: "bg-purple-100 text-purple-700",
  mechanics: "bg-red-100 text-red-700",
  roofing: "bg-slate-100 text-slate-700",
  "pest-control": "bg-green-100 text-green-700",
  remodeling: "bg-amber-100 text-amber-700",
  landscaping: "bg-emerald-100 text-emerald-700",
  moving: "bg-indigo-100 text-indigo-700",
  "garage-door": "bg-gray-100 text-gray-700",
  "appliance-repair": "bg-teal-100 text-teal-700",
};

const services = [
  { value: "plumbing", label: "Plumbing" },
  { value: "electrical", label: "Electrical" },
  { value: "hvac", label: "HVAC" },
  { value: "handyman", label: "Handyman" },
  { value: "cleaning", label: "Cleaning" },
  { value: "mechanics", label: "Auto Mechanics" },
  { value: "roofing", label: "Roofing" },
  { value: "pest-control", label: "Pest Control" },
  { value: "remodeling", label: "Remodeling" },
  { value: "landscaping", label: "Landscaping" },
  { value: "moving", label: "Moving" },
  { value: "garage-door", label: "Garage Door" },
  { value: "appliance-repair", label: "Appliance Repair" },
];

const PublicJobBoard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showMyJobs, setShowMyJobs] = useState(false);
  const [showMyJobsModal, setShowMyJobsModal] = useState(false);
  const [myJobsEmail, setMyJobsEmail] = useState("");
  const [loadingMyJobs, setLoadingMyJobs] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    service: searchParams.get("service") || "",
    location: searchParams.get("location") || ""
  });
  
  // Post Job Form
  const [postForm, setPostForm] = useState({
    title: "",
    description: "",
    service_category: "plumbing",
    location: "",
    budget_min: "",
    budget_max: "",
    customer_name: "",
    customer_email: "",
    customer_phone: ""
  });

  // Apply Form
  const [applyForm, setApplyForm] = useState({
    worker_id: "",
    message: "",
    proposed_price: "",
    estimated_duration: ""
  });

  const fetchJobs = async () => {
    setLoading(true);
    try {
      let url = `${API}/jobs?status=open`;
      if (filters.service) url += `&service=${filters.service}`;
      if (filters.location) url += `&location=${encodeURIComponent(filters.location)}`;
      
      const response = await axios.get(url);
      setJobs(response.data);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const savedWorkerId = localStorage.getItem("workerId");
    if (savedWorkerId) {
      setApplyForm(prev => ({ ...prev, worker_id: savedWorkerId }));
    }
  }, []);

  const fetchMyJobs = async (email) => {
    if (!email) return;
    setLoadingMyJobs(true);
    try {
      const response = await axios.get(`${API}/jobs/by-email/${encodeURIComponent(email)}`);
      setMyJobs(response.data);
      setShowMyJobs(true);
      setShowMyJobsModal(false);
    } catch (error) {
      console.error("Error fetching my jobs:", error);
      toast.error("Failed to load your jobs");
    } finally {
      setLoadingMyJobs(false);
    }
  };

  const handlePostJob = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/jobs`, {
        ...postForm,
        budget_min: postForm.budget_min ? parseFloat(postForm.budget_min) : null,
        budget_max: postForm.budget_max ? parseFloat(postForm.budget_max) : null
      });
      
      toast.success("Job posted successfully! Contractors will be notified.");
      setShowPostModal(false);
      setPostForm({
        title: "",
        description: "",
        service_category: "plumbing",
        location: "",
        budget_min: "",
        budget_max: "",
        customer_name: "",
        customer_email: "",
        customer_phone: ""
      });
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to post job");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApplyToJob = async (e) => {
    e.preventDefault();
    
    if (!applyForm.worker_id) {
      toast.error("Please login as a contractor first");
      navigate("/leads");
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/jobs/apply`, {
        job_id: selectedJob.id,
        worker_id: applyForm.worker_id,
        message: applyForm.message,
        proposed_price: applyForm.proposed_price ? parseFloat(applyForm.proposed_price) : null,
        estimated_duration: applyForm.estimated_duration || null
      });
      
      toast.success("Application submitted! The customer will be notified.");
      setShowApplyModal(false);
      setApplyForm(prev => ({
        ...prev,
        message: "",
        proposed_price: "",
        estimated_duration: ""
      }));
      setSelectedJob(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply");
    } finally {
      setSubmitting(false);
    }
  };

  const openApplyModal = (job) => {
    setSelectedJob(job);
    setShowApplyModal(true);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    if (newFilters.service) params.set("service", newFilters.service);
    if (newFilters.location) params.set("location", newFilters.location);
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="job-board-page">
      <Navbar />

      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-b from-[#f8f9fa] to-white">
        <div className="container-custom text-center">
          <h1 className="text-3xl md:text-4xl font-medium text-[#202124] mb-4">
            Job Board
          </h1>
          <p className="text-[#5f6368] max-w-2xl mx-auto mb-8">
            Homeowners post jobs, contractors apply. Find your next project or post a job request.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setShowPostModal(true)}
              className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full px-8 py-3 h-auto"
              data-testid="post-job-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Post a Job
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowMyJobsModal(true)}
              className="rounded-full px-8 py-3 h-auto border-[#1a73e8] text-[#1a73e8] hover:bg-[#e8f0fe]"
              data-testid="track-jobs-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              Track My Jobs
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/join")}
              className="rounded-full px-8 py-3 h-auto border-[#dadce0]"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Join as Contractor
            </Button>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-b border-[#dadce0]">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="flex items-center gap-2 text-[#5f6368]">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filter:</span>
            </div>
            
            <Select
              value={filters.service || "all"}
              onValueChange={(value) => handleFilterChange("service", value === "all" ? "" : value)}
            >
              <SelectTrigger className="w-[180px] rounded-full border-[#dadce0]" data-testid="service-filter">
                <SelectValue placeholder="All Services" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {services.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5f6368]" />
              <Input
                placeholder="Search by location..."
                value={filters.location}
                onChange={(e) => handleFilterChange("location", e.target.value)}
                className="pl-10 rounded-full border-[#dadce0]"
                data-testid="location-filter"
              />
            </div>

            {(filters.service || filters.location) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilters({ service: "", location: "" });
                  setSearchParams({});
                }}
                className="text-[#5f6368]"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}

            <div className="ml-auto text-sm text-[#5f6368]">
              {jobs.length} job{jobs.length !== 1 && "s"} found
            </div>
          </div>
        </div>
      </section>

      {/* Jobs List */}
      <section className="py-12">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#1a73e8]" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-16">
              <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-[#202124] mb-2">No jobs found</h3>
              <p className="text-[#5f6368] mb-6">
                {filters.service || filters.location
                  ? "Try adjusting your filters"
                  : "Be the first to post a job!"}
              </p>
              <Button
                onClick={() => setShowPostModal(true)}
                className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Post a Job
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {jobs.map((job) => {
                const Icon = serviceIcons[job.service_category];
                return (
                  <div
                    key={job.id}
                    className="bg-white rounded-2xl border border-[#dadce0] p-6 hover:shadow-lg transition-shadow"
                    data-testid={`job-card-${job.id}`}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${serviceColors[job.service_category]}`}>
                        {Icon && <Icon className="w-4 h-4" />}
                        {job.service_category.charAt(0).toUpperCase() + job.service_category.slice(1)}
                      </span>
                      <span className="text-xs text-[#5f6368]">
                        {new Date(job.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    {/* Title & Description */}
                    <h3 className="text-lg font-medium text-[#202124] mb-2">{job.title}</h3>
                    <p className="text-[#5f6368] text-sm mb-4 line-clamp-3">{job.description}</p>

                    {/* Details */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-[#5f6368]">
                        <MapPin className="w-4 h-4" />
                        {job.location}
                      </div>
                      {(job.budget_min || job.budget_max) && (
                        <div className="flex items-center gap-2 text-sm text-[#5f6368]">
                          <DollarSign className="w-4 h-4" />
                          Budget: ${job.budget_min || 0} - ${job.budget_max || "Flexible"}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm text-[#5f6368]">
                        <User className="w-4 h-4" />
                        {job.customer_name}
                      </div>
                    </div>

                    {/* Applications Count */}
                    <div className="flex items-center justify-between pt-4 border-t border-[#dadce0]">
                      <span className="text-sm text-[#5f6368]">
                        {job.applications?.length || 0} application{(job.applications?.length || 0) !== 1 && "s"}
                      </span>
                      <Button
                        onClick={() => openApplyModal(job)}
                        className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full"
                        data-testid={`apply-job-${job.id}`}
                      >
                        Apply Now
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Post Job Modal */}
      <Dialog open={showPostModal} onOpenChange={setShowPostModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white" data-testid="post-job-modal">
          <DialogHeader>
            <DialogTitle className="text-[#202124]">Post a Job</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePostJob} className="space-y-4">
            <div>
              <Label className="text-[#202124]">Job Title</Label>
              <Input
                value={postForm.title}
                onChange={(e) => setPostForm({...postForm, title: e.target.value})}
                placeholder="e.g., Fix leaky faucet"
                required
                className="bg-white text-[#202124] border-[#dadce0] placeholder:text-[#9aa0a6]"
                data-testid="job-title-input"
              />
            </div>
            
            <div>
              <Label className="text-[#202124]">Service Category</Label>
              <Select
                value={postForm.service_category}
                onValueChange={(value) => setPostForm({...postForm, service_category: value})}
              >
                <SelectTrigger className="bg-white text-[#202124] border-[#dadce0]" data-testid="job-service-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {services.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-[#202124]">Description</Label>
              <Textarea
                value={postForm.description}
                onChange={(e) => setPostForm({...postForm, description: e.target.value})}
                placeholder="Describe the job in detail..."
                rows={4}
                required
                className="bg-white text-[#202124] border-[#dadce0] placeholder:text-[#9aa0a6]"
                data-testid="job-description-input"
              />
            </div>
            
            <div>
              <Label className="text-[#202124]">Location</Label>
              <Input
                value={postForm.location}
                onChange={(e) => setPostForm({...postForm, location: e.target.value})}
                placeholder="City, State"
                required
                className="bg-white text-[#202124] border-[#dadce0] placeholder:text-[#9aa0a6]"
                data-testid="job-location-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#202124]">Min Budget ($)</Label>
                <Input
                  type="number"
                  value={postForm.budget_min}
                  onChange={(e) => setPostForm({...postForm, budget_min: e.target.value})}
                  placeholder="100"
                  className="bg-white text-[#202124] border-[#dadce0] placeholder:text-[#9aa0a6]"
                  data-testid="job-budget-min"
                />
              </div>
              <div>
                <Label className="text-[#202124]">Max Budget ($)</Label>
                <Input
                  type="number"
                  value={postForm.budget_max}
                  onChange={(e) => setPostForm({...postForm, budget_max: e.target.value})}
                  placeholder="500"
                  className="bg-white text-[#202124] border-[#dadce0] placeholder:text-[#9aa0a6]"
                  data-testid="job-budget-max"
                />
              </div>
            </div>

            <hr className="my-4 border-[#dadce0]" />
            <p className="text-sm text-[#5f6368]">Your contact information (shown to contractors who apply)</p>
            
            <div>
              <Label className="text-[#202124]">Your Name</Label>
              <Input
                value={postForm.customer_name}
                onChange={(e) => setPostForm({...postForm, customer_name: e.target.value})}
                required
                className="bg-white text-[#202124] border-[#dadce0] placeholder:text-[#9aa0a6]"
                data-testid="job-customer-name"
              />
            </div>
            
            <div>
              <Label className="text-[#202124]">Your Email</Label>
              <Input
                type="email"
                value={postForm.customer_email}
                onChange={(e) => setPostForm({...postForm, customer_email: e.target.value})}
                required
                className="bg-white text-[#202124] border-[#dadce0] placeholder:text-[#9aa0a6]"
                data-testid="job-customer-email"
              />
            </div>
            
            <div>
              <Label className="text-[#202124]">Your Phone</Label>
              <Input
                type="tel"
                value={postForm.customer_phone}
                onChange={(e) => setPostForm({...postForm, customer_phone: e.target.value})}
                required
                className="bg-white text-[#202124] border-[#dadce0] placeholder:text-[#9aa0a6]"
                data-testid="job-customer-phone"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full"
              data-testid="submit-job-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post Job"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Apply Modal */}
      <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
        <DialogContent className="max-w-lg bg-white" data-testid="apply-job-modal">
          <DialogHeader>
            <DialogTitle className="text-[#202124]">Apply to Job</DialogTitle>
          </DialogHeader>
          
          {selectedJob && (
            <div className="mb-4 p-4 bg-[#f8f9fa] rounded-xl">
              <h3 className="font-medium text-[#202124]">{selectedJob.title}</h3>
              <p className="text-sm text-[#5f6368]">{selectedJob.location}</p>
            </div>
          )}

          {!applyForm.worker_id ? (
            <div className="text-center py-4">
              <p className="text-[#5f6368] mb-4">You need to be logged in as a contractor to apply.</p>
              <Button
                onClick={() => navigate("/leads")}
                className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full"
              >
                Login as Contractor
              </Button>
            </div>
          ) : (
            <form onSubmit={handleApplyToJob} className="space-y-4">
              <div>
                <Label className="text-[#202124]">Your Message</Label>
                <Textarea
                  value={applyForm.message}
                  onChange={(e) => setApplyForm({...applyForm, message: e.target.value})}
                  placeholder="Introduce yourself and explain why you're the best fit..."
                  rows={4}
                  required
                  className="bg-white text-[#202124] border-[#dadce0] placeholder:text-[#9aa0a6]"
                  data-testid="apply-message"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#202124]">Your Quote ($)</Label>
                  <Input
                    type="number"
                    value={applyForm.proposed_price}
                    onChange={(e) => setApplyForm({...applyForm, proposed_price: e.target.value})}
                    placeholder="250"
                    className="bg-white text-[#202124] border-[#dadce0] placeholder:text-[#9aa0a6]"
                    data-testid="apply-price"
                  />
                </div>
                <div>
                  <Label className="text-[#202124]">Estimated Duration</Label>
                  <Input
                    value={applyForm.estimated_duration}
                    onChange={(e) => setApplyForm({...applyForm, estimated_duration: e.target.value})}
                    placeholder="2-3 hours"
                    className="bg-white text-[#202124] border-[#dadce0] placeholder:text-[#9aa0a6]"
                    data-testid="apply-duration"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full"
                data-testid="submit-application-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Track My Jobs Modal */}
      <Dialog open={showMyJobsModal} onOpenChange={setShowMyJobsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Track Your Posted Jobs</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5f6368] mb-4">
            Enter the email address you used when posting your job to see all your jobs and their applications.
          </p>
          <form onSubmit={(e) => { e.preventDefault(); fetchMyJobs(myJobsEmail); }} className="space-y-4">
            <div>
              <Label>Your Email</Label>
              <Input
                type="email"
                value={myJobsEmail}
                onChange={(e) => setMyJobsEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-1"
                data-testid="track-jobs-email"
              />
            </div>
            <Button
              type="submit"
              disabled={loadingMyJobs}
              className="w-full bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full"
              data-testid="track-jobs-submit"
            >
              {loadingMyJobs ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Find My Jobs
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* My Jobs Section (shown after email lookup) */}
      {showMyJobs && (
        <Dialog open={showMyJobs} onOpenChange={setShowMyJobs}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">Your Posted Jobs</DialogTitle>
            </DialogHeader>
            
            {myJobs.length === 0 ? (
              <div className="py-8 text-center">
                <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-[#5f6368]">No jobs found for this email</p>
                <p className="text-sm text-[#5f6368] mt-1">Try a different email or post a new job</p>
              </div>
            ) : (
              <div className="space-y-4">
                {myJobs.map((job) => {
                  const ServiceIcon = serviceIcons[job.service_category] || Wrench;
                  return (
                    <div key={job.id} className="border border-[#dadce0] rounded-xl p-4" data-testid={`my-job-${job.id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#e8f0fe] flex items-center justify-center">
                            <ServiceIcon className="w-5 h-5 text-[#1a73e8]" />
                          </div>
                          <div>
                            <h3 className="font-medium text-[#202124]">{job.title}</h3>
                            <p className="text-sm text-[#5f6368]">{job.location}</p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          job.status === "open" 
                            ? "bg-green-100 text-green-700" 
                            : job.status === "in_progress"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-700"
                        }`}>
                          {job.status.replace("_", " ").charAt(0).toUpperCase() + job.status.slice(1).replace("_", " ")}
                        </span>
                      </div>
                      
                      <p className="text-sm text-[#5f6368] mb-3 line-clamp-2">{job.description}</p>
                      
                      {job.budget_min && job.budget_max && (
                        <p className="text-sm text-[#1a73e8] mb-3">
                          Budget: ${job.budget_min} - ${job.budget_max}
                        </p>
                      )}
                      
                      {/* Applications Section */}
                      <div className="border-t border-[#dadce0] pt-3 mt-3">
                        <h4 className="font-medium text-sm text-[#202124] mb-2">
                          Applications ({job.applications_count || 0})
                        </h4>
                        {job.applications && job.applications.length > 0 ? (
                          <div className="space-y-2">
                            {job.applications.map((app) => (
                              <div key={app.id} className="bg-[#f8f9fa] rounded-lg p-3">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-sm text-[#202124]">
                                    {app.worker_name || "Contractor"}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                                    app.status === "accepted" 
                                      ? "bg-green-100 text-green-700"
                                      : app.status === "rejected"
                                      ? "bg-red-100 text-red-700"
                                      : "bg-yellow-100 text-yellow-700"
                                  }`}>
                                    {app.status}
                                  </span>
                                </div>
                                {app.proposed_price && (
                                  <p className="text-sm text-[#1a73e8]">Quote: ${app.proposed_price}</p>
                                )}
                                <p className="text-xs text-[#5f6368] mt-1 line-clamp-2">{app.message}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-[#5f6368]">No applications yet</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      <Footer />
    </div>
  );
};

export default PublicJobBoard;
